import { Box, CircularProgress, Container, Grid, Stack, ThemeProvider, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import { AnalyseMonitoringData } from '@/components/form-elements/MonitoringFilter';
import { PublicMonitoringFilter } from '@/components/form-elements/PublicMonitoringFilter';
import { FullHeightLayout } from '@/components/layouts/FullHeightLayout';
import { ActiveInactivePlot } from '@/monitoring/plots/ActiveInactivePlot';
import { CarbonDioxidBadge } from '@/monitoring/plots/CarbonDioxidBadge';
import { CarbonDioxidePlot } from '@/monitoring/plots/CarbonDioxidePlot';
import { DistancePlot } from '@/monitoring/plots/DistancePlot';
import { ModaSplitProgressPlot } from '@/monitoring/plots/ModaSplitProgressPlot';
import { ModalSplitPlot } from '@/monitoring/plots/ModalSplitPlot';
import { MovementAmount } from '@/monitoring/plots/MovementAmount';
import { ReducedCarbonDioxidePlot } from '@/monitoring/plots/ReducedCarbonDioxidePlot';
import { QueryKeys } from '@/services/QueryKeys';
import { MonitoringService } from '@/services/api';
import { themeBesserZurArbeit } from '@/setup/theme';

const filtersDefaultValues: AnalyseMonitoringData = {
  networkId: null as unknown as undefined,
  adminLevelId: null as unknown as undefined,
  start: dayjs().subtract(1, 'year').startOf('year'),
  end: dayjs().subtract(1, 'year').endOf('year'),
  includeWayBack: true,
};

export function MonitoringDashboardPage() {
  const { organizationId } = useParams();
  filtersDefaultValues.organizationId = organizationId !== undefined ? organizationId : (null as unknown as undefined);
  const [filters, setFilters] = useState<AnalyseMonitoringData>(filtersDefaultValues);
  const [showIcons, setShowIcons] = useState(true);
  const [showSubtitle, setShowSubtitle] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const language = searchParams.get('language') || 'de';
  const [englishVersion, setEnglishVersion] = useState(language === 'en');

  const { t, i18n } = useTranslation();
  if (i18n.language !== language) i18n.changeLanguage(language);

  const methods = useForm({ defaultValues: filtersDefaultValues });

  const { data, isLoading, error } = useQuery({
    queryKey: QueryKeys.monitoring.allFilters(filters),
    queryFn: () =>
      MonitoringService.publicAnalysis({
        requestBody: {
          adminLevelId: filters.adminLevelId,
          networkId: filters.networkId,
          start: filters.start.toISOString(),
          end: filters.end.toISOString(),
          includeWayBack: filters.includeWayBack,
        },
      }),
    refetchOnWindowFocus: false,
    ...(autoRefresh && { refetchInterval: 30000 }),
  });

  useEffect(() => {
    function sendHeightToParent() {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage({ type: 'setHeight', height }, '*');
    }

    window.addEventListener('load', sendHeightToParent);
    let timeout: NodeJS.Timeout | undefined;
    window.addEventListener('resize', () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(sendHeightToParent, 100);
    });
    window.addEventListener('message', (event) => {
      if (event.data.type === 'triggerSendHeightToParent') {
        sendHeightToParent();
      }
    });
  });

  return (
    <ThemeProvider theme={themeBesserZurArbeit}>
      <FullHeightLayout>
        <Container>
          <FormProvider {...methods}>
            <form>
              <PublicMonitoringFilter
                defaultValues={filtersDefaultValues}
                onFilter={(formData) => {
                  setFilters(formData);
                }}
                showIcons={showIcons}
                setShowIcons={setShowIcons}
                showSubtitle={showSubtitle}
                setShowSubtitle={setShowSubtitle}
                autoRefresh={autoRefresh}
                setAutoRefresh={setAutoRefresh}
                monitoringDataLength={data?.reduce(
                  (prev, curr) => prev + (filters.includeWayBack ? curr.count / 2 : curr.count),
                  0,
                )}
                englishVersion={englishVersion}
                setEnglishVersion={(newValue) => {
                  const newLanguage = newValue ? 'en' : 'de';
                  i18n.changeLanguage(newLanguage);

                  setSearchParams(newValue ? { language: 'en' } : {});

                  setEnglishVersion(newValue);
                }}
              />
            </form>
          </FormProvider>
          <Stack alignItems="center">
            {isLoading && <CircularProgress />}
            {!isLoading && error && (
              <Typography sx={{ marginTop: 2 }}>{`${t('MonitoringAnalysisError')} (${error.message})`}</Typography>
            )}
            {!isLoading && error && data && data.length === 0 && (
              <Typography sx={{ marginTop: 2 }}>{t('MonitoringAnalysisTooLessSamples')}</Typography>
            )}
          </Stack>
          {!isLoading && !error && data && data.length > 0 && (
            <Grid container spacing={1} sx={{ marginTop: 1 }}>
              <Grid item xs={12} md={6}>
                <CarbonDioxidBadge extrapolation={false} data={data} reduced={false} />
              </Grid>
              <Grid item xs={12} md={6}>
                <CarbonDioxidBadge extrapolation={false} data={data} reduced />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ backgroundColor: '#ededed' }}>
                  <ModalSplitPlot data={data} showSubtitle={showSubtitle} disableDownloadButton />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ backgroundColor: '#ededed' }}>
                  <ActiveInactivePlot data={data} showSubtitle={showSubtitle} disableDownloadButton />
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Box sx={{ backgroundColor: '#ededed' }}>
                  <DistancePlot
                    data={data}
                    showIcons={showIcons}
                    includeWayBack={filters.includeWayBack}
                    showSubtitle={showSubtitle}
                    disableDownloadButton
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ backgroundColor: '#ededed' }}>
                  <MovementAmount data={data} showIcons={showIcons} showSubtitle={showSubtitle} disableDownloadButton />
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Box sx={{ backgroundColor: '#ededed' }}>
                  <CarbonDioxidePlot
                    data={data}
                    showIcons={showIcons}
                    showSubtitle={showSubtitle}
                    disableDownloadButton
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ backgroundColor: '#ededed' }}>
                  <ReducedCarbonDioxidePlot
                    data={data}
                    showIcons={showIcons}
                    showSubtitle={showSubtitle}
                    disableDownloadButton
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ backgroundColor: '#ededed' }}>
                  <ModaSplitProgressPlot
                    data={data.map((commuteTypeData) => commuteTypeData.data).flat()}
                    showSubtitle={showSubtitle}
                    disableDownloadButton
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </Container>
      </FullHeightLayout>
    </ThemeProvider>
  );
}
